// @ts-check
// The Academy error vocabulary, and the one function that decides what a failed
// response actually was.
//
// See docs/API-CONTRACT.md §2. Three things about this API's errors are not
// visible from the status code, and each one has a way of failing quietly:
//
//   1. THE MACHINE CODE IS IN `title`, NOT `type`. Branching on `type` gets you
//      an RFC URL that is the same for every business failure.
//
//   2. TWO PAIRS SHARE A STATUS AND NEED OPPOSITE HANDLING.
//        stale_write vs idempotency_mismatch  — both 409. Retrying the first is
//        correct after a reload; retrying the second can NEVER succeed, and a
//        client that only reads the status retry-loops forever.
//        actor_unmatched vs a policy denial   — both 403, and only one of them
//        is the user's to fix. The policy denial has NO BODY AT ALL; the
//        unmatched actor has a ProblemDetails body carrying that title.
//
//   3. MODEL-BINDING FAILURES DO NOT USE THE VOCABULARY. [ApiController] rejects
//      a malformed body before any Academy code runs, and produces a title of
//      "One or more validation errors occurred." with a populated `errors` map.
//      Detect that shape by `errors`, never by `title`.
//
// And 429 is not ProblemDetails at all — it is `{ "error": "..." }`, with no
// Retry-After header.

/**
 * @typedef {import('@academy-api').ErrorCode} ErrorCode
 * @typedef {import('@academy-api').ProblemDetails} ProblemDetails
 */

/** The closed set from Api/Common/ErrorCodes.cs. */
export const ErrorCodes = /** @type {const} */ ({
    NotFound: 'not_found',
    Conflict: 'conflict',
    Validation: 'validation',
    Forbidden: 'forbidden',
    SeatCapacity: 'seat_capacity',
    // The COMPANY's allowance is spent — not the cohort's seats. Both are 409
    // and both say "full", and the recoveries are opposites: another cohort
    // fixes seat_capacity, and nothing but buying seats fixes this one. Never
    // offer the cohort list for it.
    SeatAllowance: 'seat_allowance',
    CurriculumFrozen: 'curriculum_frozen',
    // A programme credential refused: the holder does not meet the programme
    // bar. The detail names the unmet modules — render it verbatim.
    RequirementsUnmet: 'requirements_unmet',
    // The programme has no mandatory modules, so there is no bar to meet. 422,
    // and an ADMIN setup gap — never word it as the learner falling short.
    ProgramUnconfigured: 'program_unconfigured',
    AlreadyEnrolled: 'already_enrolled',
    NotConfirmable: 'not_confirmable',
    AiUnavailable: 'ai_unavailable',
    // The app-wide or per-learner AI call budget is spent for the period.
    // DELIBERATELY NOT AiUnavailable: that means "not configured, ask an
    // administrator"; this means "configured and working, try again next
    // period". 429, and (unusually for a 429) it IS ProblemDetails — see
    // toAcademyError's own comment on why the 429 branch checks for `title`.
    AiQuotaReached: 'ai_quota_reached',
    ArtifactUnavailable: 'artifact_unavailable',
    // Conferencing is unconfigured on this deployment. Distinct from the
    // conflict a learner gets for arriving early: waiting fixes that one and
    // fixes nothing about this one, so the card says so instead of counting
    // down to a room that will never open.
    LiveUnavailable: 'live_unavailable',
    CredentialLapsed: 'credential_lapsed',
    StaleWrite: 'stale_write',
    IdempotencyMismatch: 'idempotency_mismatch',
    ActorUnmatched: 'actor_unmatched',
    EmailUnverified: 'email_unverified',
    InstructorDelisted: 'instructor_delisted',
    CohortClosed: 'cohort_closed',
    QuizUnconfigured: 'quiz_unconfigured',

    // ── Academy Finance (phase 4) ───────────────────────────────────────────

    // No payment provider is configured on this deployment, so the checkout is
    // CLOSED. 503, and it is nobody's to retry: the buyer cannot fix it and
    // neither can waiting. Say the checkout is unavailable and offer a way to
    // reach a human — never a "try again" button.
    PaymentUnavailable: 'payment_unavailable',

    // The plan is not one the price book sells here — a stale page, or a
    // membership retired since the card was rendered. Re-read the plans and let
    // them pick again.
    PlanUnknown: 'plan_unknown',

    // A ticked programme has no price under this membership here, or nothing
    // was ticked. Re-read the plans — each lists the programmes it sells — and
    // let them tick again.
    ProgramUnpriced: 'program_unpriced',

    // A promotion code that does not exist, has expired, is exhausted, or does
    // not apply to this plan or market. ONE CODE FOR ALL FOUR, by design on the
    // server: naming which would turn the field into an oracle for enumerating
    // live codes. Keep the quote, drop the code, say it was not accepted.
    PromotionInvalid: 'promotion_invalid',

    // A confirmation arrived for an order whose quote had run out. 409, and
    // NOT retryable: the money may have moved while the order did not, and a
    // human reconciles it. A browser should never see this — it is the payment
    // provider's answer — and it is listed so a screen that does can say
    // something true rather than "conflict".
    OrderExpired: 'order_expired',

    // The provider captured an amount or a currency that is not what the order
    // asked for. Also the provider's answer, also never retryable.
    AmountMismatch: 'amount_mismatch',
});

// Codes this client synthesises. They are NOT server codes and deliberately do
// not collide with the vocabulary above — a screen that switches on a code
// should be able to handle "the request never arrived" in the same switch.
export const ClientErrorCodes = /** @type {const} */ ({
    Unauthenticated: 'unauthenticated',
    RateLimited: 'rate_limited',
    ModelBinding: 'model_binding',
    Network: 'network',
    Unknown: 'unknown',
});

/**
 * Every failure that leaves the HTTP layer is one of these. Screens branch on
 * `.code`; they never touch `error.response.data`.
 */
export class AcademyApiError extends Error {
    /**
     * @param {object} init
     * @param {string} init.code
     * @param {number} init.status
     * @param {string} init.message
     * @param {Record<string, string[]>} [init.fieldErrors] Populated only for model-binding failures.
     * @param {string} [init.traceId]
     * @param {unknown} [init.cause]
     */
    constructor({ code, status, message, fieldErrors, traceId, cause }) {
        super(message);
        this.name = 'AcademyApiError';
        this.code = code;
        this.status = status;
        this.fieldErrors = fieldErrors;
        this.traceId = traceId;
        this.cause = cause;
    }

    /**
     * True when resubmitting the SAME request could plausibly succeed. Note what
     * is absent: idempotency_mismatch is never retryable — the key is already
     * spent on a different body — and stale_write is not retryable *as-is*
     * either. It needs a reload and a reapply first, which is `needsReload`.
     */
    get isRetryable() {
        return this.code === ClientErrorCodes.Network
            || this.code === ClientErrorCodes.RateLimited
            || this.status >= 500;
    }

    /** stale_write: someone else committed first. Reload, reapply, resubmit. */
    get needsReload() {
        return this.code === ErrorCodes.StaleWrite;
    }

    /**
     * A valid token with no linked Person. Signing out and back in NEVER fixes
     * this — an administrator has to link the identity. Distinguishing it from a
     * plain policy denial is the difference between a useful message and an
     * endless sign-out/sign-in loop.
     */
    get needsProvisioning() {
        return this.code === ErrorCodes.ActorUnmatched;
    }
}

/**
 * Normalises anything axios rejects with into an AcademyApiError.
 * @param {any} error
 * @returns {AcademyApiError}
 */
export function toAcademyError(error) {
    if (error instanceof AcademyApiError) return error;

    // No response at all: DNS, connection refused, CORS rejection, timeout. The
    // browser deliberately hides which, so this cannot be narrowed further.
    if (!error?.response) {
        return new AcademyApiError({
            code: ClientErrorCodes.Network,
            status: 0,
            message: error?.message || 'The server could not be reached.',
            cause: error,
        });
    }

    const { status, data } = error.response;

    // 429 is usually the rate limiter's own `{ "error": "..." }` shape, not
    // ProblemDetails — but ai_quota_reached (ACADEMY-AI-WORKFLOWS-PLAN §7.2)
    // is a 429 from ApiResults.Problem, which DOES carry a title. Check for
    // that shape first so an AI quota refusal keeps its own code and locale
    // message instead of collapsing into the generic rate-limit one.
    if (status === 429 && typeof data?.title !== 'string') {
        return new AcademyApiError({
            code: ClientErrorCodes.RateLimited,
            status,
            message: data?.error || 'Too many requests. Please try again later.',
            cause: error,
        });
    }

    // Model binding. Detected by the presence of `errors`, per the contract —
    // its title is prose, not a code, and matching on it would break the moment
    // ASP.NET changes the wording.
    if (data && typeof data === 'object' && data.errors) {
        return new AcademyApiError({
            code: ClientErrorCodes.ModelBinding,
            status,
            message: 'The request body was rejected before it reached the API.',
            fieldErrors: data.errors,
            traceId: data.traceId,
            cause: error,
        });
    }

    // A policy denial from the authorization middleware carries NO BODY. An
    // actor_unmatched carries one. Both are 403 and they mean opposite things.
    if (status === 403 && !data?.title) {
        return new AcademyApiError({
            code: ErrorCodes.Forbidden,
            status,
            message: 'You do not have permission to do that.',
            cause: error,
        });
    }

    if (status === 401) {
        return new AcademyApiError({
            code: ClientErrorCodes.Unauthenticated,
            status,
            message: data?.detail || 'A valid bearer token is required.',
            cause: error,
        });
    }

    // The normal path: ProblemDetails from ApiResults, code in `title`.
    const title = typeof data?.title === 'string' ? data.title : null;
    return new AcademyApiError({
        code: title ?? ClientErrorCodes.Unknown,
        status,
        message: data?.detail || title || `Request failed with status ${status}.`,
        traceId: data?.traceId,
        cause: error,
    });
}

/**
 * i18n key for an error, so a screen can render it without a switch statement.
 * Falls back to the generic key when the dictionary has no entry — an unknown
 * code must never render as the raw code.
 * @param {AcademyApiError} error
 * @param {(key: string) => string} t
 */
export function errorMessage(error, t) {
    const key = `error.${error.code}`;
    const translated = t(key);
    if (translated !== key) return translated;
    // `detail` is written for a human and names the actual cohort or module, so
    // it beats a generic string when we have one.
    return error.message || t('error.unknown');
}
